import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { style } from './vehicle-review.style.js';
import { VehicleReviewService } from '../../services/vehicle-review.service.js';
import { SessionService } from '../../core/services/session/session.service.js';
import { DebugService } from '../../shared/services/debug.service.js';
import { stratum } from '../../../constants/stratum.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../shared/constants/app.constants.js';
import { ApplicationService } from '../../services/application.service.js';

class RefiVehicleReview extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiVehicleReview');
  }

  static properties = {
    year: { type: String },
    make: { type: String },
    model: { type: String },
    vin: { type: String },
    experiments: {
      type: Object,
      hasChanged: (newVal, oldVal) => {
        if (newVal !== oldVal) {
          const experimentIds = [];
          Object.keys(newVal).forEach((experiment) => {
            if (newVal[experiment].isEligible) {
              experimentIds.push(newVal[experiment].allocation);
            } else {
              experimentIds.push(`${experiment}-ineligible`);
            }
          });
          ApplicationService.updateExperiments(experimentIds);
          ApplicationService.updateExperiments(
            experimentIds,
            this.existingExperimentIds
          );
          this.existingExperimentIds = experimentIds;
          return true;
        }
        return false;
      },
    },
  };

  static styles = [style];

  connectedCallback() {
    super.connectedCallback();
    const eAppId = SessionService.session?.eAppId;

    VehicleReviewService.getVehicleInfo(eAppId)
      .then((vehicleInfo) => {
        this.year = vehicleInfo.year;
        this.make = vehicleInfo.make;
        this.model = vehicleInfo.model;
        this.vin = vehicleInfo.vin;
        this.dispatchCompleteLoadingEvent();
        if (this.year && this.make && this.model && this.vin) {
          stratum.publishTag(TagKey.CAR_CHECKOUT__VAL_MFE_LOAD, {
            replacements: {
              value: 'successful',
            },
          });
        } else {
          stratum.publishTag(TagKey.CAR_CHECKOUT__VAL_MFE_LOAD, {
            replacements: {
              value: 'failed',
            },
          });
        }
      })
      .catch((err) => {
        this.debug.log('Failed to retrieve vehicle for review: ', err.message);
        this.dispatchFailedToLoadOrUpdateEvent();
        stratum.publishTag(TagKey.CAR_CHECKOUT__VAL_MFE_LOAD, {
          replacements: {
            value: 'failed',
          },
        });
      });

    VehicleReviewService.getStips(eAppId)
      .then((response) => {
        const titleStip = response.find(
          (stip) =>
            stip.taskType.toUpperCase() ===
            REFI_VEHICLE_LOAN_CONST.STIP_TYPE.TITLE.toUpperCase()
        );
        if (!titleStip) {
          this.debug.log('Missing Title stip info after call to Stips API');
        }
        if (
          titleStip.taskStatus.toUpperCase() ===
          REFI_VEHICLE_LOAN_CONST.STIP_INCOMPLETE
        ) {
          this.dispatchEvent(
            new CustomEvent('checkout-update-mudflap', {
              bubbles: true,
              composed: true,
              detail: {
                actionName: 'update-mudflap',
                id: 'car-card',
                text: 'You’ll need to mail us your title to finalize financing; we’ll assist later',
              },
            })
          );
        }
        stratum.publishTag(TagKey.CAR_CHECKOUT__TITLE_STIP_MUDFLAP_LOADS, {
          replacements: {
            value: `successful - ${titleStip.taskStatus}`,
          },
        });
      })
      .catch((err) => {
        this.debug.log(
          'Failed to retrieve stip information for vehicle review on checkout: ',
          err.message
        );
        stratum.publishTag(TagKey.CAR_CHECKOUT__TITLE_STIP_MUDFLAP_LOADS, {
          replacements: {
            value: 'failed',
          },
        });
      });
  }

  dispatchCompleteLoadingEvent() {
    this.dispatchEvent(
      new CustomEvent('checkout-completed-loading', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'car-card',
        },
      })
    );
    this.debug.log('completed loading event fired');
  }

  dispatchFailedToLoadOrUpdateEvent() {
    this.dispatchEvent(
      new CustomEvent('failed-load-or-update', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'car-card',
        },
      })
    );
    this.debug.log('failed to load or update event fired');
  }

  render() {
    return this.year && this.make && this.model && this.vin
      ? html` <div class="vehicle-review__contents">
          <fs-body-text variant="primary">
            ${this.year} ${this.make} ${this.model}
          </fs-body-text>
          <fs-body-text variant="primary">${this.vin}</fs-body-text>
        </div>`
      : null;
  }
}

customElements.define('refi-vehicle-review', RefiVehicleReview);
